import type { EntitySummaryViewData } from '@shared/types/views';
import {
  AlertTriangle,
  BookOpen,
  Lightbulb,
  TrendingUp,
} from 'lucide-react';
import { ExpandableText } from '@/components/ui/ExpandableText';
import { StatusBadge } from '@/components/views/atoms/StatusBadge';
import { LabelValueList } from '@/components/views/atoms/view-grammar';
import { formatDueDate } from '@/lib/format-due-date';

const ENTITY_TYPE_LABELS: Record<EntitySummaryViewData['entity']['type'], string> = {
  account: 'Compte', company: 'Entreprise', contact: 'Contact', deal: 'Affaire',
};

/** Short facts read best as a compact list; long ones keep their card. */
const SHORT_FACT_CHARS = 80;

interface EntitySummaryViewProps {
  data: EntitySummaryViewData;
}

const splitFactValue = (value: string): string[] => {
  const normalized = value
    .split(/\n|•/g)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);

  if (normalized.length > 1) {
    return normalized;
  }

  return value
    .split(/(?<=[.!?])\s+/g)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
};

const resolveFactIcon = (label: string) => {
  const normalized = label.toLowerCase();
  if (normalized.includes('objection') || normalized.includes('risque')) {
    return AlertTriangle;
  }
  if (normalized.includes('signal')) {
    return TrendingUp;
  }
  if (normalized.includes('contexte')) {
    return Lightbulb;
  }
  return BookOpen;
};

export const EntitySummaryView = ({ data }: EntitySummaryViewProps) => {
  const shortFacts = data.key_facts.every((fact) => fact.value.length <= SHORT_FACT_CHARS && !/\n|•/.test(fact.value));
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-lg font-semibold tracking-tight text-domino-ink">{data.entity.name}</p>
          <p className="text-xs text-domino-inkSoft">{ENTITY_TYPE_LABELS[data.entity.type] ?? data.entity.type}</p>
        </div>
        {data.health && <StatusBadge status={data.health.status} label={`${data.health.score}/100`} />}
      </div>
      {data.tags && data.tags.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label="Étiquettes">
          {data.tags.map((tag) => <li key={tag} className="rounded-full bg-domino-muted px-2.5 py-0.5 text-xs text-domino-ink">{tag}</li>)}
        </ul>
      )}
      {data.headline && (
        <ExpandableText
          text={data.headline}
          collapsedLines={5}
          minCharactersToToggle={260}
          className="max-w-3xl text-sm leading-6 text-domino-inkSoft"
        />
      )}

      {data.key_facts.length > 0 && shortFacts && (
        <LabelValueList rows={data.key_facts.map((fact) => ({ label: fact.label, value: fact.value }))} />
      )}

      {data.key_facts.length > 0 && !shortFacts && (
        <div
          className="grid gap-x-8 border-t border-domino-cardBorder"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}
        >
          {data.key_facts.map((fact) => (
            <div
              key={`${fact.label}-${fact.value}`}
              className="min-w-0 border-b border-domino-cardBorder py-4"
            >
              <div className="flex items-center gap-2">
                {(() => {
                  const Icon = resolveFactIcon(fact.label);
                  return <Icon className="h-4 w-4 text-domino-aubergine" />;
                })()}
                <p className="text-sm font-semibold text-domino-ink">{fact.label}</p>
              </div>
              <ul className="mt-2 space-y-1.5">
                {splitFactValue(fact.value).map((line) => (
                  <li key={line} className="flex gap-2 text-sm leading-6 text-domino-inkSoft">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-domino-prune" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {data.recent_activity && data.recent_activity.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-domino-ink">Activité récente</p>
          <ul className="mt-2 space-y-2">
            {data.recent_activity.map((entry) => (
              <li key={`${entry.date}-${entry.summary}`} className="flex gap-3 text-sm leading-6">
                <span className="w-16 shrink-0 text-domino-inkSoft">{formatDueDate(entry.date).label}</span>
                <span className="min-w-0 text-domino-ink">{entry.summary}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
